// src/context/AuthContext.jsx
// -----------------------------------------------------------------------------
// Global authentication context. Wraps the whole app (see main.jsx) so any
// component can read the logged-in user / role and call login()/logout()
// without prop-drilling.
//
// Persists the JWT + user object in localStorage so a page refresh does not
// log the user out.
//
// IMPORTANT - matches the REAL Afix.Api backend (AuthController -> LoginService.Login):
//   - It always replies with HTTP 200, even on bad credentials. Failure is
//     signalled by the response BODY being a plain string:
//       "Invalid Email"  |  "Invalid Password"
//     instead of a proper 4xx status. We detect that here and throw so the
//     UI can show the real reason.
//   - On success the body looks like:
//       { Message, UserId, Token, First_Name, Email, Phone_1, Role, IsPasswordupdated }
//     (flat, PascalCase, a single "Token" - no accessToken/refreshToken, no
//     nested "user" object). We normalize that into the camelCase shape the
//     rest of the app (Header, Sidebar, Dashboard, ProtectedRoute) expects:
//       { userID, firstName, lastName, email, phone, roleName, isPasswordUpdated }
//   - If the account still has IsPasswordupdated = false, the backend returns
//     { ForcePasswordChange: true, UserId, Message } instead of a token - we
//     surface that as a distinct error so the Login page can react to it.
//
// DEMO ACCOUNT:
//   loginDemo() below is a local-only shortcut for demoing the UI when there's
//   no real "demo@afix.com" row in the database. It does NOT call the API - it
//   sets the exact same context state + localStorage keys a real login()
//   would, so ProtectedRoute / Header / Sidebar all keep working normally.
//   Remove this function (and its button in Login.jsx) before going to
//   production if you don't want a client-side login bypass in the bundle.
// -----------------------------------------------------------------------------
import { createContext, useContext, useState, useEffect } from 'react';
import authService from '../api/authService';

const AuthContext = createContext(null);

// Role name constants - keep in sync with the Login.Role values stored in tblLogin.
export const ROLES = {
  ADMIN: 'Admin',
  PROJECT_MANAGER: 'Project Manager',
  TEAM_MEMBER: 'Team Member',
  CLIENT: 'Client',
};



export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On first load, restore the session from localStorage (if any).
  useEffect(() => {
    const storedUser = localStorage.getItem('afix_user');
    const storedToken = localStorage.getItem('afix_access_token');
    if (storedUser && storedToken) {
      try {
        const parsedUser = JSON.parse(storedUser);
        // Session upgrade: ensure userID is set if stored as userId or id previously
        if (parsedUser && !parsedUser.userID) {
          parsedUser.userID = parsedUser.userId || parsedUser.id;
          localStorage.setItem('afix_user', JSON.stringify(parsedUser));
        }
        setUser(parsedUser);
      } catch {
        localStorage.removeItem('afix_user');
        localStorage.removeItem('afix_access_token');
      }
    }
    setLoading(false);
  }, []);

  const persistAndSetUser = (normalizedUser, token) => {
    localStorage.setItem('afix_access_token', token);
    localStorage.setItem('afix_user', JSON.stringify(normalizedUser));
    setUser(normalizedUser);
  };

  /** Calls the backend login endpoint and stores the token + user on success. */
  const login = async (email, password) => {

    const response = await authService.login(email, password);
    console.log("Afix API Login success response body:", response.data);
    console.log("Afix API Login success response headers:", response.headers);
    const data = response.data;

    // Backend signals failure with a 200 OK + a plain string body instead of
    // a 4xx status code - catch that here so it doesn't slip through as a
    // "successful" login with undefined fields.
    if (typeof data === 'string') {
      throw new Error(data); // "Invalid Email" or "Invalid Password"
    }

    if (data?.ForcePasswordChange || data?.forcePasswordChange) {
      const err = new Error(data.Message || data.message || 'Please change your password before continuing.');
      err.forcePasswordChange = true;
      err.userId = data.UserId !== undefined ? data.UserId : data.userId;
      throw err;
    }

    // Normalize the backend's raw PascalCase/underscore fields into the
    // camelCase shape used everywhere else in the app.
    const normalizedUser = {
      userID: data.UserId !== undefined ? data.UserId : (data.userId !== undefined ? data.userId : data.id),
      firstName: data.First_Name || data.first_Name || data.firstName || '',
      lastName: data.Last_Name || data.last_Name || data.lastName || '',
      email: data.Email || data.email || '',
      phone: data.Phone_1 || data.phone_1 || data.phone || '',
      roleName: data.Role || data.role || '',
      isPasswordUpdated: data.IsPasswordupdated !== undefined ? data.IsPasswordupdated : data.isPasswordupdated,
    };

    const token =
      data.Token ||
      data.token ||
      data.tokenString ||
      data.accessToken ||
      response.headers?.['authorization']?.replace('Bearer ', '') ||
      response.headers?.['Authorization']?.replace('Bearer ', '') ||
      response.headers?.['token'] ||
      response.headers?.['Token'];

    persistAndSetUser(normalizedUser, token);
    return normalizedUser;
  };

  /** Clears the session locally (and best-effort on the backend). */
  const logout = () => {
    authService.logout().catch(() => {
      /* ignore network errors on logout */
    });
    localStorage.removeItem('afix_access_token');
    localStorage.removeItem('afix_refresh_token');
    localStorage.removeItem('afix_user');
    setUser(null);
  };

  /** Helper: true if the logged-in user's base role matches one of "roles". */
  const hasRole = (...roles) => !!user && roles.includes(user.roleName);

  const value = { user, loading, login, logout, hasRole };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hook to access auth state anywhere: const { user, logout } = useAuth(); */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}